import { SlideElement, AspectRatio } from '../types';

// 레이아웃 옵션 인터페이스
export interface LayoutOption {
  name: string;
  type: string;
  columns: number[];
  description?: string;
}

// 객체 수별 레이아웃 옵션 정의
export const getLayoutOptionsForObjectCount = (objectCount: number): LayoutOption[] => {
  const options: LayoutOption[] = [];
  
  switch (objectCount) {
    case 1:
      options.push({
        name: '1열 배열',
        type: 'single-column',
        columns: [1],
      });
      break;
      
    case 2:
      options.push(
        {
          name: '1열 배열',
          type: 'single-column',
          columns: [2],
        },
        {
          name: '2열 배열 (좌1, 우1)',
          type: 'two-column-equal',
          columns: [1, 1],
        }
      );
      break;
      
    case 3:
      options.push(
        {
          name: '1열 배열',
          type: 'single-column',
          columns: [3],
        },
        {
          name: '2열 배열 (좌1, 우2)',
          type: 'two-column-1-2',
          columns: [1, 2],
        },
        {
          name: '2열 배열 (좌2, 우1)',
          type: 'two-column-2-1',
          columns: [2, 1],
        },
        {
          name: '3열 배열',
          type: 'three-column',
          columns: [1, 1, 1],
        }
      );
      break;
      
    case 4:
      options.push(
        {
          name: '1열 배열',
          type: 'single-column',
          columns: [4],
        },
        {
          name: '2열 배열 (좌2, 우2)',
          type: 'two-column-equal',
          columns: [2, 2],
        },
        {
          name: '2열 배열 (좌1, 우3)',
          type: 'two-column-1-3',
          columns: [1, 3],
        },
        {
          name: '3열 배열 (1:2:1)',
          type: 'three-column-1-2-1',
          columns: [1, 2, 1],
        },
        {
          name: '4열 배열',
          type: 'four-column',
          columns: [1, 1, 1, 1],
        }
      );
      break;
      
    case 5:
      options.push(
        {
          name: '2열 배열 (좌1, 우4)',
          type: 'two-column-1-4',
          columns: [1, 4],
        },
        {
          name: '2열 배열 (좌2, 우3)',
          type: 'two-column-2-3',
          columns: [2, 3],
        },
        {
          name: '2열 배열 (좌3, 우2)',
          type: 'two-column-3-2',
          columns: [3, 2],
        },
        {
          name: '3열 배열 (1:2:2)',
          type: 'three-column-1-2-2',
          columns: [1, 2, 2],
        },
        {
          name: '5열 배열',
          type: 'five-column',
          columns: [1, 1, 1, 1, 1],
        }
      );
      break;
      
    case 6:
      options.push(
        {
          name: '2열 배열 (좌2, 우4)',
          type: 'two-column-2-4',
          columns: [2, 4],
        },
        {
          name: '2열 배열 (좌3, 우3)',
          type: 'two-column-equal',
          columns: [3, 3],
        },
        {
          name: '3열 배열 (1:2:3)',
          type: 'three-column-1-2-3',
          columns: [1, 2, 3],
        },
        {
          name: '3열 배열 (2:2:2)',
          type: 'three-column-equal',
          columns: [2, 2, 2],
        },
        {
          name: '6열 배열',
          type: 'six-column',
          columns: [1, 1, 1, 1, 1, 1],
        }
      );
      break;
      
    default: {
      // 6개 초과시 기본 레이아웃
      const cols = Math.ceil(Math.sqrt(objectCount));
      const distribution = [];
      for (let i = 0; i < cols; i++) {
        distribution.push(Math.ceil(objectCount / cols));
      }
      options.push({
        name: `${cols}열 배열`,
        type: 'auto-grid',
        columns: distribution,
      });
      break;
    }
  }
  
  return options;
};

// 콘텐츠 객체 개수 계산 (제목 포함/제외 옵션)
export const countContentObjects = (elements: SlideElement[], slideHeight = 600, includeTitle = false): number => {
  if (!elements) {return 0;}
  
  // 도형을 제외한 객체들 카운트
  return elements.filter(element => {
    // 도형 제외 (shape 타입)
    if (element.type === 'shape') {
      return false;
    }
    
    // 제목 처리 (includeTitle 옵션에 따라)
    if (element.type === 'text' && isTitle(element)) {
      return includeTitle; // includeTitle이 true면 포함, false면 제외
    }
    
    // 명시적 title 타입 처리
    if (element.type === 'title') {
      return includeTitle; // includeTitle이 true면 포함, false면 제외
    }
    
    return true;
  }).length;
};

// 제목인지 판별하는 함수
const isTitle = (element: SlideElement): boolean => {
  // 명시적으로 title 타입인 경우
  if (element.type === 'title') {return true;}
  
  // textType이 'title'인 경우
  if (element.type === 'text' && element.textType === 'title') {return true;}
  
  // id에 'title'이 포함된 경우 (레거시 지원)
  if (element.type === 'text' && element.id && element.id.includes('title')) {return true;}
  
  // AI가 생성한 텍스트는 content가 있고 특정 포맷이 있으므로 제목이 아님
  if (element.type === 'text' && element.content && (element.content.includes('<') || element.content.includes('•') || element.content.includes('1.'))) {
    return false;
  }
  
  // 위치나 크기로 판단하지 않음 - 제목은 명시적으로만 판단
  return false;
};

// 슬라이드 테마에 따른 콘텐츠 영역 계산
export const getContentArea = (
  aspectRatio: AspectRatio,
  slideTheme = 'default',
  splitPosition = 50
) => {
  const width = aspectRatio.width;
  const height = aspectRatio.height;
  
  // 제목 영역 (상단 15%)
  const titleHeight = height * 0.15;
  
  const contentArea = {
    x: 0,
    y: titleHeight,
    width: width,
    height: height - titleHeight
  };
  
  // 슬라이드 테마에 따라 실제 콘텐츠 영역 제한 padding 배율은 절대 조절하지 않습니다.
  const splitRatio = splitPosition / 100;
  const padding = width * 0.02; // 2% 패딩으로 증가
  
  switch (slideTheme) {
    case 'left-split':
      // 좌측은 배경, 우측이 콘텐츠 영역 (패딩 적용하여 전체 영역 활용)
      contentArea.x = width * splitRatio + padding;
      contentArea.y = padding;
      contentArea.width = width * (1 - splitRatio) - padding * 1;
      contentArea.height = height - padding * 2;
      break;
      
    case 'right-split':
      // 우측은 배경, 좌측이 콘텐츠 영역 (패딩 적용하여 전체 영역 활용)
      contentArea.x = padding;
      contentArea.y = padding;
      contentArea.width = width * (1 - splitRatio) - padding * 1;
      contentArea.height = height - padding * 2;
      break;
      
    case 'top-split':
      // 상단은 배경, 하단이 콘텐츠 영역 (패딩 적용하여 전체 영역 활용)
      contentArea.x = padding;
      contentArea.y = height * splitRatio + padding;
      contentArea.width = width - padding * 2;
      contentArea.height = height * (1 - splitRatio) - padding * 1;
      break;
      
    case 'bottom-split':
      // 하단은 배경, 상단이 콘텐츠 영역 (패딩 적용하여 전체 영역 활용)
      contentArea.x = padding;
      contentArea.y = padding;
      contentArea.width = width - padding * 2;
      contentArea.height = height * (1 - splitRatio) - padding * 1;
      break;
      
    case 'diagonal': {
      // 사선형: 제목 영역을 splitPosition% + 15%로 설정
      const diagonalTitleHeight = height * ((splitPosition / 100) + 0.15);
      contentArea.x = padding;
      contentArea.y = diagonalTitleHeight + padding;
      contentArea.width = width - padding * 2;
      contentArea.height = height - diagonalTitleHeight - padding * 2;
      break;
    }
      
    case 'image-card':
      // 이미지 카드: 전체 영역 사용 (아래 이미지 배경 제외)
      contentArea.x = padding;
      contentArea.y = padding;
      contentArea.width = width - padding * 2;
      contentArea.height = height * 0.7 - padding; // 상단 70%만 사용 (하단 30%는 이미지)
      break;
      
    default:
      // 기본형: 더 넓은 콘텐츠 영역 사용
      contentArea.x = padding;
      contentArea.y = titleHeight + padding;
      contentArea.width = width - padding * 2;
      contentArea.height = height - titleHeight - padding * 2;
      break;
  }
  
  return contentArea;
};

// 슬라이드 테마에 따른 배경 영역 계산
export const getBackgroundArea = (
  aspectRatio: AspectRatio,
  slideTheme = 'default',
  splitPosition = 50
) => {
  const width = aspectRatio.width;
  const height = aspectRatio.height;
  const splitRatio = splitPosition / 100;
  const padding = width * 0.02; // 2% 패딩
  
  const backgroundArea = {
    x: 0,
    y: 0,
    width: width,
    height: height
  };
  
  switch (slideTheme) {
    case 'left-split':
      // 좌측이 배경 영역
      backgroundArea.x = padding;
      backgroundArea.y = padding;
      backgroundArea.width = width * splitRatio - padding * 2;
      backgroundArea.height = height - padding * 2;
      break;
      
    case 'right-split':
      // 우측이 배경 영역
      backgroundArea.x = width * (1 - splitRatio) + padding;
      backgroundArea.y = padding;
      backgroundArea.width = width * splitRatio - padding * 2;
      backgroundArea.height = height - padding * 2;
      break;
      
    case 'top-split':
      // 상단이 배경 영역
      backgroundArea.x = padding;
      backgroundArea.y = padding;
      backgroundArea.width = width - padding * 2;
      backgroundArea.height = height * splitRatio - padding * 2;
      break;
      
    case 'bottom-split':
      // 하단이 배경 영역
      backgroundArea.x = padding;
      backgroundArea.y = height * (1 - splitRatio) + padding;
      backgroundArea.width = width - padding * 2;
      backgroundArea.height = height * splitRatio - padding * 2;
      break;
      
    case 'diagonal':
      // 사선형: splitPosition에 따라 왼쪽 상단 또는 오른쪽 영역
      if (splitPosition < 50) {
        // 왼쪽 상단 영역
        backgroundArea.x = padding;
        backgroundArea.y = padding;
        backgroundArea.width = width * 0.5 - padding;
        backgroundArea.height = height * 0.5 - padding;
      } else {
        // 오른쪽 영역
        backgroundArea.x = width * 0.4 + padding;
        backgroundArea.y = padding;
        backgroundArea.width = width * 0.6 - padding * 2;
        backgroundArea.height = height - padding * 2;
      }
      break;
      
    case 'image-card':
      // 이미지 카드: 하단 30%가 배경 영역
      backgroundArea.x = padding;
      backgroundArea.y = height * 0.7;
      backgroundArea.width = width - padding * 2;
      backgroundArea.height = height * 0.3 - padding;
      break;
      
    default:
      // 기본형: 상단 15%가 제목 영역
      backgroundArea.x = padding;
      backgroundArea.y = padding;
      backgroundArea.width = width - padding * 2;
      backgroundArea.height = height * 0.15 - padding;
      break;
  }
  
  return backgroundArea;
};

// 배경 영역에서의 제목 위치 계산
export const getTitlePositionInBackground = (
  backgroundArea: { x: number; y: number; width: number; height: number },
  slideTheme = 'default',
  aspectRatio: AspectRatio
) => {
  // 배경 영역 크기 비율 계산
  const areaRatio = (backgroundArea.width * backgroundArea.height) / (aspectRatio.width * aspectRatio.height);
  
  // 테마별 제목 위치 조정 - 배경 영역보다 가로 세로 2%씩 작게
  let titleX = backgroundArea.x + backgroundArea.width * 0.02; // 좌측 2% 여백
  let titleY = backgroundArea.y + backgroundArea.height * 0.02; // 상단 2% 여백
  let titleWidth = backgroundArea.width * 0.96; // 가로 96% (좌우 각각 2% 여백)
  let titleHeight = backgroundArea.height * 0.96; // 세로 96% (상하 각각 2% 여백)
  
  // 제목 객체 크기에 따른 반응형 폰트 크기 조정
  let fontSize: number;
  
  // 제목 객체의 실제 크기 계산 (픽셀 단위)
  const titleWidthPx = titleWidth;
  const titleHeightPx = titleHeight;
  
  // 객체 크기에 따른 폰트 크기 계산
  // 높이의 20-30%를 폰트 크기로 사용 (최소 12px, 최대 60px)
  const heightBasedFontSize = titleHeightPx * 0.25;
  const widthBasedFontSize = titleWidthPx * 0.1; // 너비의 10%를 폰트 크기로
  
  // 높이와 너비 중 작은 값 기준으로 폰트 크기 결정
  fontSize = Math.min(heightBasedFontSize, widthBasedFontSize);
  
  // 최소/최대 크기 제한
  fontSize = Math.max(12, Math.min(60, fontSize));
  
  // 배경 영역이 매우 작은 경우 특별 처리
  if (areaRatio < 0.05) {
    fontSize = Math.max(10, titleHeightPx * 0.3); // 더 작은 영역에서는 높이의 30%
  }
  
  // 테마별 특별 처리
  switch (slideTheme) {
    case 'left-split':
    case 'right-split':
      // 세로 분할: 중앙 정렬
      titleY = backgroundArea.y + backgroundArea.height * 0.05;
      
      // 좌은 영역 특별 처리 - 2% 여백 유지
      if (backgroundArea.width < aspectRatio.width * 0.2) {
        titleX = backgroundArea.x + backgroundArea.width * 0.02; // 좌측 2% 여백
        titleWidth = backgroundArea.width * 0.96; // 가로 96%
        titleHeight = backgroundArea.height * 0.96; // 세로 96%
        titleY = backgroundArea.y + backgroundArea.height * 0.02; // 상단 2% 여백
        
        // 작은 영역에서의 폰트 크기 재계산
        const smallTitleWidthPx = titleWidth;
        const smallTitleHeightPx = titleHeight;
        const smallHeightBasedFontSize = smallTitleHeightPx * 0.3; // 높이의 30%
        const smallWidthBasedFontSize = smallTitleWidthPx * 0.15; // 너비의 15%
        fontSize = Math.min(smallHeightBasedFontSize, smallWidthBasedFontSize);
        fontSize = Math.max(10, Math.min(40, fontSize));
      }
      break;
      
    case 'top-split':
    case 'bottom-split':
      // 가로 분할: 중앙 정렬
      titleY = backgroundArea.y + backgroundArea.height * 0.02; // 상단 2% 여백
      titleHeight = backgroundArea.height * 0.96; // 세로 96%
      
      // 좌은 영역 특별 처리
      if (backgroundArea.height < aspectRatio.height * 0.2) {
        titleY = backgroundArea.y + backgroundArea.height * 0.02; // 상단 2% 여백
        titleHeight = backgroundArea.height * 0.96; // 세로 96%
        
        // 작은 영역에서의 폰트 크기 재계산
        const smallTitleWidthPx = titleWidth;
        const smallTitleHeightPx = titleHeight;
        const smallHeightBasedFontSize = smallTitleHeightPx * 0.3; // 높이의 30%
        const smallWidthBasedFontSize = smallTitleWidthPx * 0.15; // 너비의 15%
        fontSize = Math.min(smallHeightBasedFontSize, smallWidthBasedFontSize);
        fontSize = Math.max(10, Math.min(40, fontSize));
      }
      break;
      
    case 'diagonal':
      // 사선형: 배경 영역에 맞게 조정
      titleY = backgroundArea.y + backgroundArea.height * 0.02; // 상단 2% 여백
      titleHeight = backgroundArea.height * 0.96; // 세로 96%
      break;
      
    case 'image-card': {
      // 이미지 카드: 특별 처리 - 상단에 오버레이로 표시
      const imageCardWidth = 0.8;
      const imageCardHeight = 0.15;
      const imageCardFontSize = Math.min(
        aspectRatio.height * imageCardHeight * 0.25, // 높이의 25%
        aspectRatio.width * imageCardWidth * 0.1 // 너비의 10%
      );
      return {
        x: 0.1,
        y: 0.1,
        width: imageCardWidth,
        height: imageCardHeight,
        fontSize: Math.max(12, Math.min(40, imageCardFontSize)),
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        padding: '8px 12px',
        borderRadius: '4px'
      };
    }
      
    case 'default': {
      // 기본형: 상위 15% 영역 고정
      const defaultWidth = 0.8;
      const defaultHeight = 0.1;
      const defaultFontSize = Math.min(
        aspectRatio.height * defaultHeight * 0.25, // 높이의 25%
        aspectRatio.width * defaultWidth * 0.1 // 너비의 10%
      );
      return {
        x: 0.1,
        y: 0.05,
        width: defaultWidth,
        height: defaultHeight,
        fontSize: Math.max(12, Math.min(40, defaultFontSize))
      };
    }
  }
  
  // 최소 크기 보장
  const minTitleWidth = 100 / aspectRatio.width;
  const minTitleHeight = 40 / aspectRatio.height;
  
  if (titleWidth < minTitleWidth) {
    titleWidth = minTitleWidth;
    // 필요시 위치 조정
    if (titleX + titleWidth > backgroundArea.x + backgroundArea.width) {
      titleX = backgroundArea.x + (backgroundArea.width - titleWidth) / 2;
    }
  }
  
  if (titleHeight < minTitleHeight) {
    titleHeight = minTitleHeight;
  }
  
  // 비율 좌표로 변환 (0-1)
  return {
    x: titleX / aspectRatio.width,
    y: titleY / aspectRatio.height,
    width: titleWidth / aspectRatio.width,
    height: titleHeight / aspectRatio.height,
    fontSize: fontSize,
    // 가독성을 위한 추가 스타일
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
    wordWrap: 'break-word',
    lineHeight: 1.2,
    maxLines: areaRatio < 0.15 ? 3 : 2
  };
};

// 레이아웃에 따른 객체 위치 계산
export const calculateObjectPositions = (
  layoutOption: LayoutOption,
  objectCount: number,
  contentArea: { x: number; y: number; width: number; height: number },
  slideWidth?: number,
  slideHeight?: number,
  spacing = 0.02
): Array<{ x: number; y: number; width: number; height: number }> => {
  const positions: Array<{ x: number; y: number; width: number; height: number }> = [];
  const margin = spacing; // 간격 설정 사용
  
  // layoutOption이 없거나 columns가 없는 경우 기본 레이아웃 사용
  if (!layoutOption || !layoutOption.columns || !Array.isArray(layoutOption.columns)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[calculateObjectPositions] Invalid layoutOption, using default single column layout');
    }
    // 기본 단일 컬럼 레이아웃으로 폴백
    for (let i = 0; i < objectCount; i++) {
      const itemHeight = (contentArea.height - contentArea.height * margin * (objectCount + 1)) / objectCount;
      const x = contentArea.x + contentArea.width * margin;
      const y = contentArea.y + contentArea.height * margin + i * (itemHeight + contentArea.height * margin);
      const width = contentArea.width - contentArea.width * margin * 2;
      const height = itemHeight;
      
      // slideWidth와 slideHeight가 전달되면 비율로 변환
      if (slideWidth && slideHeight) {
        positions.push({
          x: x / slideWidth,
          y: y / slideHeight,
          width: width / slideWidth,
          height: height / slideHeight
        });
      } else {
        // 없으면 픽셀 좌표 그대로
        positions.push({ x, y, width, height });
      }
    }
    return positions;
  }
  
  const { columns } = layoutOption;
  const numCols = columns.length;
  
  // 컬럼별 너비 계산 (마진 포함)
  const totalMarginWidth = contentArea.width * margin * (numCols + 1);
  const availableWidth = contentArea.width - totalMarginWidth;
  const colWidths = columns.map(() => availableWidth / numCols);
  
  let objectIndex = 0;
  
  // 각 컬럼별로 객체 배치
  for (let colIndex = 0; colIndex < numCols && objectIndex < objectCount; colIndex++) {
    const itemsInCol = columns[colIndex];
    const colWidth = colWidths[colIndex];
    const colX = contentArea.x + contentArea.width * margin + 
                 colIndex * (colWidth + contentArea.width * margin);
    
    // 컬럼 내 아이템 높이 계산
    const totalMarginHeight = contentArea.height * margin * (itemsInCol + 1);
    const availableHeight = contentArea.height - totalMarginHeight;
    const itemHeight = availableHeight / itemsInCol;
    
    // 컬럼 내 각 아이템 배치
    for (let itemIndex = 0; itemIndex < itemsInCol && objectIndex < objectCount; itemIndex++) {
      const itemY = contentArea.y + contentArea.height * margin + 
                    itemIndex * (itemHeight + contentArea.height * margin);
      
      // 픽셀 좌표를 비율 좌표(0-1)로 변환
      // slideWidth와 slideHeight가 전달되지 않으면 비율로 반환
      if (slideWidth && slideHeight) {
        positions.push({
          x: colX / slideWidth,
          y: itemY / slideHeight,
          width: colWidth / slideWidth,
          height: itemHeight / slideHeight
        });
      } else {
        // slideWidth/Height가 없으면 픽셀 좌표 그대로 반환
        positions.push({
          x: colX,
          y: itemY,
          width: colWidth,
          height: itemHeight
        });
      }
      
      objectIndex++;
    }
  }
  
  return positions;
};

// 기본 레이아웃 선택 (첫 번째 옵션)
export const getDefaultLayoutOption = (objectCount: number): LayoutOption => {
  const options = getLayoutOptionsForObjectCount(objectCount);
  return options[0] || {
    name: '1열 배열',
    type: 'single-column',
    columns: [objectCount],
  };
};

// 동적 레이아웃 적용 함수
// [주석 처리] 이 함수는 하드코딩된 해상도를 사용하고 splitPosition을 고려하지 않아 사용하지 않음
// SlidesContainer.tsx에서 직접 calculateObjectPositions를 사용하는 것으로 대체됨
/*
export const applyDynamicLayout = (slide: any, layoutOption: LayoutOption): any => {
  if (!slide.elements || slide.elements.length === 0) {
    return slide;
  }

  const contentArea = getContentArea(
    { width: 1920, height: 1080, label: '16:9', value: '16:9' }, // 기본 해상도 사용
    slide.slideTheme || 'default'
  );

  const positions = calculateObjectPositions(
    layoutOption,
    slide.elements.length,
    contentArea,
    1920, // 기본 슬라이드 너비
    1080  // 기본 슬라이드 높이
  );

  // 요소들의 위치를 업데이트
  const updatedElements = slide.elements.map((element: any, index: number) => {
    if (index < positions.length) {
      return {
        ...element,
        x: positions[index].x,
        y: positions[index].y,
        width: positions[index].width,
        height: positions[index].height
      };
    }
    return element;
  });

  return {
    ...slide,
    elements: updatedElements
  };
};
*/